import {
  getIsTransactionLimitOnlyEnabled,
  LimitsInConstraintGroupFormValue,
} from '@occc/feat-limits';
import { ProductChallengeEarningMechanism } from '../../../types';
import { ChallengeType } from '../../../constants';

export const getIsProgressPerPeriodLimitSupported = (challengeType: ChallengeType | null) => {
  return challengeType === ChallengeType.VisitChallenge;
};

export const getIsProgressPerTransactionLimitSupported = (
  challengeType: ChallengeType | null,
  earningMechanismType: ProductChallengeEarningMechanism | undefined,
) => {
  return (
    challengeType === ChallengeType.ProductChallenge &&
    earningMechanismType === 'varietyPurchaseOverTime'
  );
};

export const getIsTransactionLimitSupported = (
  challengeType: ChallengeType | null,
  earningMechanismType: ProductChallengeEarningMechanism | undefined,
) => {
  return (
    challengeType === ChallengeType.ProductChallenge && earningMechanismType === 'varietyPurchase'
  );
};

export const getIsMinimumPurchaseConstraintSupported = (
  challengeType: ChallengeType | null,
  earningMechanismType: ProductChallengeEarningMechanism | undefined,
) => {
  return (
    challengeType === ChallengeType.ProductChallenge && earningMechanismType === 'varietyPurchase'
  );
};

export const getIsProgressPerTransactionLimitOnlyEnabled = (
  values: LimitsInConstraintGroupFormValue,
) => {
  return (
    values.progressPerTransaction?.isEnabled &&
    !values.global.isEnabled &&
    !values.membership.isEnabled &&
    !values.progressPerPeriod?.isEnabled &&
    !values.transaction?.isEnabled
  );
};

/**
 * Determines if the "Set Limits" section should be closed due to Progress Per Transaction Limit.
 *
 * This function evaluates whether the "Set Limits" section should be closed based on:
 * 1. If award count is enabled ("Set Limits" section is open)
 * 2. If progress per transaction limit is not supported for the given challenge type and other configurations
 * 3. If only progress per transaction limit is enabled in the "Set Limits" section
 *
 * The idea is to hide the "Set Limits" UI section when a user selects an unsupported
 * configuration where only progress per transaction is enabled but the current challenge
 * type doesn't support this limit type.
 */
export const shouldCloseSetLimitsSectionDueToProgressPerTransaction = (
  challengeType: ChallengeType | null,
  productChallengeEarningMechanism: ProductChallengeEarningMechanism | undefined,
  values: LimitsInConstraintGroupFormValue,
): boolean => {
  const isProgressPerTransactionLimitSupported = getIsProgressPerTransactionLimitSupported(
    challengeType,
    productChallengeEarningMechanism,
  );

  const isProgressPerTransactionLimitOnlyEnabled =
    getIsProgressPerTransactionLimitOnlyEnabled(values);

  if (
    values.isEnabled &&
    !isProgressPerTransactionLimitSupported &&
    isProgressPerTransactionLimitOnlyEnabled
  ) {
    return true;
  }

  return false;
};

/**
 * Determines if the "Set Limits" section should be closed due to Progress Per Period Limit.
 *
 * This function evaluates whether the "Set Limits" section should be closed based on:
 * 1. If award count is enabled ("Set Limits" section is open)
 * 2. If progress per period limit is only enabled in the "Set Limits" section
 *
 * The idea is to hide the "Set Limits" UI section when a user selects an unsupported
 * configuration where only progress per period is enabled but the current challenge
 * type doesn't support this limit type.
 */
export const shouldCloseSetLimitsSectionDueToProgressPerPeriod = (
  challengeType: ChallengeType | null,
  values: LimitsInConstraintGroupFormValue,
): boolean => {
  const isProgressPerPeriodLimitSupported = getIsProgressPerPeriodLimitSupported(challengeType);

  const isProgressPerPeriodOnlyEnabled =
    values.progressPerPeriod?.isEnabled &&
    !values.global.isEnabled &&
    !values.membership.isEnabled &&
    !values.progressPerTransaction?.isEnabled &&
    !values.transaction?.isEnabled;

  if (values.isEnabled && !isProgressPerPeriodLimitSupported && isProgressPerPeriodOnlyEnabled) {
    return true;
  }

  return false;
};

/**
 * Determines if the "Set Fulfillment Limits" section should be closed due to Transaction Limit.
 *
 * This function evaluates whether the "Set Fulfillment Limits" section should be closed based on:
 * 1. If award count is enabled ("Set Fulfillment Limits" section is open)
 * 2. If transaction limit is not supported for the given challenge type
 * 3. If only transaction limit is enabled in the "Set Fulfillment Limits" section
 *
 * The idea is to hide the "Set Fulfillment Limits" UI section when a user selects an unsupported
 * configuration where only transaction limit is enabled but the current challenge
 * type doesn't support this limit type.
 */
export const shouldCloseSetLimitsSectionDueToTransactionLimit = (
  challengeType: ChallengeType | null,
  values: LimitsInConstraintGroupFormValue,
  earningMechanismType?: ProductChallengeEarningMechanism,
): boolean => {
  const isTransactionLimitSupported = getIsTransactionLimitSupported(
    challengeType,
    earningMechanismType,
  );
  const isTransactionLimitOnlyEnabled = getIsTransactionLimitOnlyEnabled(values);

  if (values.isEnabled && !isTransactionLimitSupported && isTransactionLimitOnlyEnabled) {
    return true;
  }

  return false;
};
